import { i18n } from '@/plugins/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/stores/ui.store';
import { handleAxiosError } from '@/utils/error.utils';
import { getAccountStrategy } from '@/utils/middleware.utils';
import { toast } from '@/utils/toast.utils';
import type { NavigationGuard } from 'vue-router';

export const authMiddlewares: Record<string, NavigationGuard> = {
  isFlokeeAccount: async (_to, _, next) => {
    const authStore = useAuthStore();

    if (authStore.isCleepoAccount) return next({ name: 'CleepoDashboardView' });

    next();
  },
  isCleepoAccount: async (_to, _, next) => {
    const authStore = useAuthStore();

    if (authStore.isFlokeeAccount) return next({ name: 'DashboardView' });

    next();
  },
  isAppLogin: async (to, _, next) => {
    const currentDomain = window.location.origin;

    if (currentDomain === import.meta.env.VITE_APP_CLEEPO_URL) {
      if (!to.path.startsWith('/cleepo')) {
        return next({ name: 'CleepoAuthLoginView' });
      }
    }

    if (currentDomain === import.meta.env.VITE_APP_FLOKEE_URL) {
      if (to.path.startsWith('/cleepo')) {
        return next({ name: 'AuthLoginView' });
      }
    }

    next();
  },
  shouldRedirectToDashboard: async (_to, _, next) => {
    const authStore = useAuthStore();
    const uiStore = useUiStore();

    uiStore.setLoading(true);

    if (authStore.access_token) {
      uiStore.setLoading(false);
      if (authStore.isFlokeeAccount) return next({ name: 'DashboardView' });
      if (authStore.isCleepoAccount) return next({ name: 'CleepoDashboardView' });
    }

    uiStore.setLoading(false);

    next();
  },
  isAuthenticated: async (_to, _, next) => {
    const authStore = useAuthStore();
    const uiStore = useUiStore();
    const { t } = i18n.global as any;

    uiStore.setLoading(true);
    if (!authStore.access_token) {
      uiStore.setLoading(false);
      authStore.lastRoutePath = _to.fullPath;
      return next({ name: 'AuthLoginView' });
    }

    if (!authStore.user) {
      try {
        await authStore.invalidate();
        await authStore.getCurrentUser();
      } catch (error) {
        localStorage.clear();
        authStore.access_token = undefined;
        toast.error({ message: t('notifications.unauthenticated_error') });
        uiStore.setLoading(false);
        authStore.lastRoutePath = _to.fullPath;
        return next({ name: 'AuthLoginView' });
      }
    }

    uiStore.setLoading(false);
    if (authStore.lastRoutePath) {
      next(authStore.lastRoutePath);
      authStore.lastRoutePath = undefined;
    } else {
      next();
    }
  },
  isAuthenticatedCleepo: async (_to, _, next) => {
    const { t } = i18n.global as any;
    const authStore = useAuthStore();
    const uiStore = useUiStore();

    uiStore.setLoading(true);
    if (!authStore.access_token) {
      uiStore.setLoading(false);
      authStore.lastRoutePath = _to.fullPath;
      return next({ name: 'CleepoAuthLoginView' });
    }

    if (!authStore.user) {
      try {
        await authStore.invalidate();
        await authStore.getCurrentUser();
      } catch (error) {
        localStorage.clear();
        authStore.access_token = undefined;
        toast.error({ message: t('notifications.unauthenticated_error') });
        uiStore.setLoading(false);
        authStore.lastRoutePath = _to.fullPath;
        return next({ name: 'CleepoAuthLoginView' });
      }
    }

    uiStore.setLoading(false);

    if (authStore.lastRoutePath) {
      next(authStore.lastRoutePath);
      authStore.lastRoutePath = undefined;
    } else {
      next();
    }
  },
  isAccountConfigured: async (_to, _, next) => {
    const authStore = useAuthStore();

    if (!authStore.user) {
      authStore.logout();
      return next({ name: 'AuthLoginView' });
    }

    const strategy = getAccountStrategy();

    // Execute validation steps
    const accountRedirect = await strategy.validateAccount();
    if (accountRedirect) {
      return next({ name: accountRedirect });
    }

    const isInvalidOrder = !authStore.user.current_order_id;

    if (isInvalidOrder) {
      const orderRedirect = await strategy.handleNoCurrentOrder();
      if (orderRedirect) {
        return next({ name: orderRedirect });
      }
    }

    if (strategy.validateIntegration) {
      const integrationRedirect = await strategy.validateIntegration();
      if (integrationRedirect) {
        return next({ name: integrationRedirect });
      }
    }

    return next();
  },
  verifyAuthentication: async (_to, _, next) => {
    const authStore = useAuthStore();
    const uiStore = useUiStore();
    uiStore.setLoading(true);

    if (!authStore.user && authStore.access_token) {
      try {
        await authStore.getCurrentUser();
      } catch (error) {
        const { message } = handleAxiosError(error);
        toast.error({ message });
      }
    }

    uiStore.setLoading(false);

    next();
  },
  isAdmin: async (_to, _, next) => {
    const authStore = useAuthStore();

    if (!authStore.user && authStore.access_token) {
      return next({ name: 'AuthLoginView' });
    }

    if (authStore.user?.role.id !== 1 && authStore.user?.role.id !== 3) {
      return next({ name: 'AuthLoginView' });
    }

    next();
  },
};
